import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Zap } from "lucide-react";
import { lineReveal } from "@/components/anim";
import { api } from "@/lib/api";

const HERO_LINES = ["The Alpha", "Hits Us First.", "You Ape Next."];

export const Hero = ({ onJoin }) => {
  const { scrollY } = useScroll();
  const yBack = useTransform(scrollY, [0, 600], [0, 180]);
  const opacityBack = useTransform(scrollY, [0, 500], [1, 0.15]);
  const [stats, setStats] = useState({ avg_gain: "340X", win_rate: "98.4%", active_degens: 4892 });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const metrics = [
    { label: "Avg Called Gain", value: stats.avg_gain },
    { label: "Signal Win Rate", value: stats.win_rate },
    { label: "Active Degens", value: Number(stats.active_degens).toLocaleString() },
  ];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden grid-bg grain radial-glow min-h-screen flex items-center pt-28 pb-16"
    >
      {/* giant parallax backdrop */}
      <motion.div
        style={{ y: yBack, opacity: opacityBack }}
        className="pointer-events-none absolute inset-0 flex items-center justify-center select-none"
        aria-hidden
      >
        <span className="font-display font-black text-[36vw] leading-none text-white/[0.02] tracking-tighter">
          500X
        </span>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-[#9945ff44] bg-[#9945ff14] px-3.5 py-1.5 mb-7"
        >
          <Zap className="w-3.5 h-3.5 text-[#9945FF]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#c9a6ff]">
            The most accurate meme coin insider group
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-5xl"
        >
          {HERO_LINES.map((line, i) => (
            <span key={i} className="line-mask block">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="show"
                className={`block ${i === 1 ? "text-[#14F195] text-glow-green" : "text-white"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-[#94A3B8] leading-relaxed"
        >
          Pre-public token airdrops, custom sub-millisecond sniper bots, and live Tier-1
          whale tracking. Pay 2 SOL once, unlock the private Telegram instantly.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            data-testid="hero-cta-claim-button"
            onClick={onJoin}
            className="group inline-flex items-center gap-2 rounded-full bg-[#14F195] px-7 py-3.5 text-base font-bold text-[#05060A] hover:bg-[#3dffb0] hover:scale-[1.03] active:scale-95 transition-transform duration-200 shadow-[0_0_40px_-6px_rgba(20,241,149,0.6)]"
          >
            Claim VIP Pass — 2 SOL
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <a
            data-testid="hero-cta-proof-button"
            href="#track"
            className="inline-flex items-center gap-2 rounded-full border border-[#ffffff1f] px-7 py-3.5 text-base font-semibold text-white hover:border-[#14f19566] hover:text-[#14F195] transition-colors duration-300"
          >
            Explore the Proof
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-16 grid grid-cols-3 max-w-2xl divide-x divide-[#ffffff12] rounded-2xl glass cyber-border"
        >
          {metrics.map((m) => (
            <div key={m.label} className="px-4 sm:px-7 py-5">
              <div className="font-display font-black text-2xl sm:text-4xl text-white">
                {m.value}
              </div>
              <div className="mt-1 font-mono text-[10px] sm:text-xs uppercase tracking-wider text-[#64748B]">
                {m.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
